import { useWallet } from "@solana/wallet-adapter-react";
import { useEffect, useState } from "react";
import * as React from "react";

import { Button, ButtonBase } from "../../../shared/src/Components/Button.tsx";
import { shorten_key } from "../../../shared/src/Functions/Utils.ts";
import styled, { css } from "styled-components";
import InlineButton from "../../../shared/src/Components/InlineButton.tsx";
import { useWalletMultiButton } from "@solana/wallet-adapter-base-ui";

type ContainerProps = {
    isOpen: boolean;
};

const Container = styled.div<ContainerProps>`
    position: relative;
    ${(props) =>
        props.isOpen &&
        css`
            ${ButtonBase}::before {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }
        `}
`;

type MenuContainerProps = {
    containerHeight: number;
};

const MenuContainer = styled.div<MenuContainerProps>`
    position: absolute;
    border-width: var(--border-width);
    border-color: var(--text);
    border-style: solid;
    border-bottom-left-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
    background: var(--background);
    width: 100%;
    display: flex;
    justify-content: center;
    top: calc(${(props) => props.containerHeight}px - var(--border-width));
    padding-inline: var(--spacing-6);
    padding-block: var(--spacing-6);
    z-index: 999;
`;

export const SelectWalletButton = ({
    walletModalConfig,
    buttonState,
    onConnect,
    onDisconnect,
    hasSubscryptoInstalled,
}: {
    walletModalConfig: ReturnType<any>;
    buttonState: ReturnType<typeof useWalletMultiButton>["buttonState"];
    hasSubscryptoInstalled: boolean | undefined;
    onConnect: ReturnType<typeof useWalletMultiButton>["onConnect"];
    onDisconnect: ReturnType<typeof useWalletMultiButton>["onDisconnect"];
}) => {
    const { publicKey } = useWallet();

    let label;
    switch (buttonState) {
        case "connected":
            label = "Disconnect";
            break;
        case "connecting":
            label = "Connecting";
            break;
        case "disconnecting":
            label = "Disconnecting";
            break;
        case "has-wallet":
            label = "Connect";
            break;
        case "no-wallet":
            label = "Select Wallet";
            break;
    }

    function getButtonContent(buttonStateProp: typeof buttonState): string {
        if (publicKey && buttonStateProp === "connected") {
            return shorten_key(publicKey.toString());
        } else if (buttonStateProp === "no-wallet" && hasSubscryptoInstalled) {
            return "Connect Subscrypto";
        } else if (buttonStateProp === "connecting") {
            return "Connecting...";
        } else if (buttonStateProp === "no-wallet" && !hasSubscryptoInstalled) {
            return "Connect wallet";
        } else if (buttonStateProp === "disconnecting") {
            return "Disconnecting...";
        } else {
            return `${buttonStateProp} + ${hasSubscryptoInstalled}`;
        }
    }

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleWalletButtonClick = () => {
        if (buttonState === "connected" || buttonState === "connecting") {
            setIsMenuOpen((prev) => !prev);
        }
        if (buttonState === "has-wallet") {
            if (onConnect) {
                onConnect();
            } else {
                console.error("No onConnect");
            }
        }
        if (buttonState === "no-wallet" && !hasSubscryptoInstalled) {
            // TODO: show popup that suggests installing the extension
        }

        if (buttonState === "no-wallet" && hasSubscryptoInstalled) {
            const subscryptoWalletAdapter = walletModalConfig.wallets.find(
                (wallet) => wallet.adapter.name === "Subscrypto"
            );
            if (subscryptoWalletAdapter) {
                console.log("found subscrypto onClick");
                walletModalConfig.onSelectWallet(subscryptoWalletAdapter.adapter.name);
            }
        }
    };

    // Handle disconnect action
    const handleDisconnect = () => {
        console.log("Disconnected!");
        setIsMenuOpen(false); // Close the popup after disconnecting
        if (onDisconnect) {
            onDisconnect();
        } else {
            console.error("Disconnecting not connected!");
        }
    };

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = React.useState<number>(0);

    useEffect(() => {
        if (containerRef.current) {
            setContainerHeight(containerRef.current.offsetHeight);
        }
    }, [containerRef, isMenuOpen]);

    return (
        <>
            <Container ref={containerRef} isOpen={isMenuOpen}>
                <Button size={"mini"} onClick={handleWalletButtonClick}>
                    {getButtonContent(buttonState)}
                </Button>
                {isMenuOpen && (
                    <MenuContainer containerHeight={containerHeight}>
                        <InlineButton onClick={handleDisconnect} size={"mini"} variant={"default"}>
                            Disconnect
                        </InlineButton>
                    </MenuContainer>
                )}
            </Container>
        </>
    );
};
